import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plane, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await login(email, password); toast.success("Welcome back!"); nav("/admin"); }
    catch (err) { toast.error(err.response?.data?.detail || "Invalid credentials"); }
    finally { setLoading(false); }
  };

  const forgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email exists, a reset code has been sent.");
      nav("/admin/reset");
    } catch { toast.error("Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B3C5D] flex items-center justify-center px-4">
      <form onSubmit={mode === "login" ? submit : forgot} className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-10 h-10 rounded-xl bg-[#0B3C5D] flex items-center justify-center"><Plane className="w-5 h-5 text-[#FF6B35]" /></span>
          <span className="font-display font-extrabold text-lg text-[#0B3C5D]">Elia Admin</span>
        </div>
        {mode === "login" ? (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900">Staff sign in</h1>
            <p className="text-slate-500 text-sm mb-6">Manage listings, bookings and enquiries.</p>
            <div className="space-y-4">
              <div><Label>Email</Label><Input data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
              <div><Label>Password</Label><Input data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} /></div>
              <Button data-testid="admin-login-btn" disabled={loading} className="w-full rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold py-6">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
              </Button>
              <button type="button" data-testid="admin-forgot-link" onClick={() => setMode("forgot")} className="text-sm text-[#0B3C5D] font-semibold w-full text-center">Forgot password?</button>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900">Forgot password</h1>
            <p className="text-slate-500 text-sm mb-6">We'll email a reset code to your admin address.</p>
            <div className="space-y-4">
              <div><Label>Admin email</Label><Input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
              <Button data-testid="forgot-submit" disabled={loading} className="w-full rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold py-6">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset code"}
              </Button>
              <div className="flex justify-between text-sm">
                <button type="button" onClick={() => setMode("login")} className="text-[#0B3C5D] font-semibold">Back to sign in</button>
                <Link to="/admin/reset" className="text-slate-400">Have a code?</Link>
              </div>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
